import React, { useState, useEffect } from 'react';
import moment from 'moment';

// Components
import Breadcrumbs from '../../../components/Breadcrumbs';
import SearchAndFilter from '../../../components/Search/SearchAndFilter';
import ViewHistoryWtItemReturn from '../../../components/Modal/ViewHistoryWtItemReturn';
import Pagination from '../../../components/Footer/Pagination';

import { formatCurrency, getAlert, parseCurrency, updateStatusByNo } from "../../../../utils/SamuiUtils";
import { formatDate } from "../../../../utils/DateUtils";
import { manageWhItemData } from "../../../../utils/WarehouseUtils";
import {
  useDataTableKeyboardNavigation,
  getDataTableRowStyle,
  getDataTableKeyboardInstructions
} from '../../../../utils/DataTableKeyboardNavigation';

const Main = ({ name, dataMasterList, dataDetailList, dataMasterHistoryList, dataDetailHistoryList, onPageInsert, onPageUpdate }) => {

  // สำหรับข้อมูลรายการ
  const [filteredDetailList, setFilteredDetailList] = useState([]);
  const [wtEmpName, setWtEmpName] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);

  // สำหรับข้อมูลประวัติการยืม
  const [filteredDetailHistoryList, setFilteredDetailHistoryList] = useState([]);
  const [whHistoryCustName, setWhHistoryCustName] = useState("");
  const [selectedHistoryRow, setSelectedHistoryRow] = useState(null);

  // เก็บข้อมูลที่เลือกรายละเอียดสินค้าที่จะคืน
  const [isCheckboxEnabled, setIsCheckboxEnabled] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showModal, setShowModal] = useState(false);

  // เก็บตัวแปรสำหรับเปิด Modal ดูประวัติ
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(
    localStorage.getItem('itemsPerPageDropdown') || 10
  );

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState(
    localStorage.getItem('searchTerm') || ''
  );
  const [filteredItems, setFilteredItems] = useState([]);

  // Date filter states
  const [startDate, setStartDate] = useState(() => {
    const storedStartDate = localStorage.getItem('startDate');
    return storedStartDate || moment().startOf('month').format('YYYY-MM-DD');
  });

  const [endDate, setEndDate] = useState(() => {
    const storedEndDate = localStorage.getItem('endDate');
    return storedEndDate || moment().endOf('month').format('YYYY-MM-DD');
  });

  // Initialize data
  useEffect(() => {
    if (dataMasterList && dataMasterList.length > 0) {
      setCurrentPage(1);
      filterItems();
    } else {
      setFilteredItems([]);
    }
  }, [dataMasterList]);

  // Filter when search term or dates change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedRowIndex(0);
    filterItems();
  }, [searchTerm, startDate, endDate, dataMasterList]);

  useEffect(() => {
    if (selectedRow) {
      const selectedEmployee = dataMasterList.find(emp => emp.WTDoc_Emp_Id === selectedRow);
      if (selectedEmployee) {
        onRowSelected(selectedEmployee);
      }
    }
  }, [startDate, endDate, dataDetailList]);

  // Filter items function
  const filterItems = () => {
    try {
      const uniqueMap = new Map();
      for (const emp of dataMasterList) {
        if (!uniqueMap.has(emp.WTDoc_Emp_Id)) {
          uniqueMap.set(emp.WTDoc_Emp_Id, emp);
        }
      }
      let filteredEmployees = Array.from(uniqueMap.values());

      filteredEmployees.sort((a, b) => {
        const codeA = (a.WTDoc_Emp_Code || '').toString().trim();
        const codeB = (b.WTDoc_Emp_Code || '').toString().trim();
        const numA = parseInt(codeA, 10);
        const numB = parseInt(codeB, 10);
        const bothNumeric = !isNaN(numA) && !isNaN(numB);
        if (bothNumeric) return numA - numB;
        return codeA.localeCompare(codeB, 'th');
      });

      if (searchTerm.trim() !== '') {
        const lowercasedSearchTerm = searchTerm.toLowerCase();
        filteredEmployees = filteredEmployees.filter((item) =>
          (item.WTDoc_Emp_Code && item.WTDoc_Emp_Code.toLowerCase().includes(lowercasedSearchTerm)) ||
          (item.WTDoc_Emp_Name && item.WTDoc_Emp_Name.toLowerCase().includes(lowercasedSearchTerm))
        );
      }

      setFilteredItems(filteredEmployees);
    } catch (error) {
      getAlert('FAILED', error.message);
      setFilteredItems([]);
    }
  };

  // Pagination calculations
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    setSelectedRowIndex(0);
    localStorage.setItem('currentPage', pageNumber);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

  const onRowSelected = async (fromData, index = -1) => {
    setSelectedRowIndex(index);
    setSelectedRow(fromData.WTDoc_Emp_Id);

    // พี่คินอยากให้กระพริบ เดี๋ยวไปร์ทจัดให้
    setFilteredDetailList([]);
    setWtEmpName("");

    setTimeout(() => {
      // กรองตาม Employee ID และวันที่ที่เลือก
      const start = moment(startDate, 'YYYY-MM-DD');
      const end = moment(endDate, 'YYYY-MM-DD');

      const filteredDetail = dataDetailList.filter(data => {
        const matchesEmployee = parseCurrency(data.WTDoc_Emp_Id) === fromData.WTDoc_Emp_Id;
        const itemDate = moment(data.WTDoc_Date, 'YYYY-MM-DD');
        const matchesDateRange = itemDate.isBetween(start, end, null, '[]');

        return matchesEmployee && matchesDateRange;
      });

      if (filteredDetail && filteredDetail.length > 0) {
        setFilteredDetailList(filteredDetail);
      } else {
        setFilteredDetailList([]);
      }

      setWtEmpName(fromData.WTDoc_Emp_Name);
    }, 50);
  };

  // Field mappings for keyboard navigation
  const fieldMappings = [
    { field: 'WTDoc_Emp_Code', label: 'รหัสพนักงาน' },
    { field: 'WTDoc_Emp_Name', label: 'ชื่อพนักงาน' }
  ];

  // Keyboard navigation
  useDataTableKeyboardNavigation(
    true, // isActive
    currentItems,
    selectedRowIndex,
    setSelectedRowIndex,
    onRowSelected,
    fieldMappings,
    currentPage,
    handlePageChange,
    filteredItems.length,
    itemsPerPage
  );

  const onRowSelectHistory = async (fromHistoryData) => {
    setSelectedHistoryRow(fromHistoryData.WTDoc_Emp_Id);

    // พี่คินอยากให้กระพริบ เดี๋ยวไปร์ทจัดให้
    setFilteredDetailHistoryList([]);
    setWhHistoryCustName("");

    setTimeout(() => {
      const filteredHistoryDetail = dataDetailHistoryList
        .filter(
          data => parseCurrency(data.WTDoc_Emp_Id) === fromHistoryData.WTDoc_Emp_Id
        );

      if (filteredHistoryDetail && filteredHistoryDetail.length > 0) {
        setFilteredDetailHistoryList(filteredHistoryDetail);
      } else {
        setFilteredDetailHistoryList([]);
      }

      setWhHistoryCustName(fromHistoryData.WTDoc_Emp_Name);
    }, 50);
  };

  const handleCheckboxChange = (itemSelected) => {
    const updatedSelectedItems = selectedItems.some(item => item.DT_Id === itemSelected.DT_Id)
      ? selectedItems.filter(item => item.DT_Id !== itemSelected.DT_Id)
      : [...selectedItems, itemSelected];
    setSelectedItems(updatedSelectedItems);
  };

  const handleConfirmReturn = async () => {
    if (selectedItems.length === 0) {
      getAlert("FAILED", "กรุณาเลือกรายการที่ต้องการคืน");
      return;
    }

    for (const selectedItem of selectedItems) {
      // คืนคลังสินค้า
      await manageWhItemData(
        selectedItem.Item_Id, // Item ไอดีของสิ่งนั้นๆ
        selectedItem.Item_Code, // Item โค้ดของสิ่งนั้นๆ
        selectedItem.Item_Name, // ชื่อ Item นั้นๆ
        selectedItem.F_WT_Id, // WarehouseID ของไอเทม
        "AI", // ประเภท เช่น IN/OUT/AI/AO/TI/TO
        parseCurrency(selectedItem.Item_Qty), // ค่าจำนวนที่กรอกจากหน้าจอ
        selectedItem.WTDoc_Id, // ไอดีเอกสาร
        selectedItem.WTDoc_No, // เลขเอกสาร
        selectedItem.WTDoc_No, // เลขเอกสารอ้างอิง
        `คืนสินค้า` // หมายเหตุ
      );

      // อัพเดตสถานะเอกสาร
      await updateStatusByNo("WTDoc_D", "Item_Is_Return", parseCurrency(1), `WHERE DT_Id = ${selectedItem.DT_Id}`);
    }

    // กรองสินค้าที่คืนไปแล้วออกจาก filteredDetailList
    // dataDetailList.filter(
    //   (item) => !selectedItems.some((selected) => selected.WTDoc_Id === item.WTDoc_Id)
    // )

    // กลับโหมดเดิม
    setIsCheckboxEnabled(false);
    setSelectedItems([]);

    // await callInitialize();
    // await onRowSelected(formData);
    // getAlert("OK", `คืนสินค้าสําเร็จ`);
    window.location.reload();
  };

  return <>
    <Breadcrumbs page={name} items={[
      { name: 'ใบยืม-ใบเบิก', url: '/borrow-withdrawal' },
      { name: name, url: '/technical-equip-form' },
    ]} />
    <div className="container-fluid">
      {/* Search and Filter Section */}
      <SearchAndFilter
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        searchPlaceholder="ค้นหารหัสพนักงาน, ชื่อพนักงาน..."
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        showDateFilter={true}
        onAddNew={onPageInsert}
        addButtonText={`สร้าง${name}`}
        showAddButton={true}
      />

      <div className="card py-3">
        <div className="row mb-2 align-items-center">
          <div className="col-10" />
          <div className="col-2 d-flex justify-content-end">
            <button
              className="btn d-flex align-items-center text-white me-2"
              style={{
                backgroundColor: 'rgb(239, 108, 0)',
                fontSize: '16px',
              }}
              onClick={() => setShowHistoryModal(true)}
            >
              <i className="fa fa-search me-2" aria-hidden="true"></i>
              ประวัติการเบิก
            </button>

            <ViewHistoryWtItemReturn
              showHistoryModal={showHistoryModal}
              setShowHistoryModal={setShowHistoryModal}
              dataMasterHistoryList={dataMasterHistoryList}
              filteredDetailHistoryList={filteredDetailHistoryList}
              selectedHistoryRow={selectedHistoryRow}
              whHistoryCustName={whHistoryCustName}
              formatCurrency={formatCurrency}
              onRowSelectHistory={onRowSelectHistory}
              onPageUpdate={onPageUpdate}
            />

          </div>
        </div>
        <hr />
        <div className="row ">
          <div className="col-4 mb-1" style={{ borderRight: '2px solid #c7c8c9' }}>
            <div className="table-responsive" style={{ maxHeight: '650px', overflowY: 'auto' }}>
              <table id="basic-datatables" className="table table-striped table-hover">
                <thead className="thead-dark">
                  <tr>
                    <th className="text-center" style={{ width: '10%' }}>ลำดับ</th>
                    <th className="text-center" style={{ width: '20%' }}>รหัสพนักงาน</th>
                    <th className="text-center" style={{ width: '70%' }}>ชื่อพนักงาน</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((data, index) => (
                      <tr
                        key={data.WTDoc_Emp_Id || index + 1}
                        onClick={() => {
                          setSelectedRowIndex(index);
                          onRowSelected(data);
                        }}
                        style={getDataTableRowStyle(
                          index,
                          selectedRowIndex,
                          selectedRow,
                          data,
                          'WTDoc_Emp_Id'
                        )}>
                        <td className="text-center">{indexOfFirstItem + index + 1}</td>
                        <td className="text-center">{data.WTDoc_Emp_Code}</td>
                        <td className="text-left">{data.WTDoc_Emp_Name}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3">
                        <center>
                          <h5>ไม่พบข้อมูล</h5>
                        </center>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination & Items per page selector */}
            <div className="bg-white mt-2" style={{ padding: '8px 0' }}>
              <div className="row m-0">
                <div className="col-12 d-flex justify-content-between align-items-center flex-wrap py-2">
                  <div className="d-flex align-items-center flex-wrap">
                    <span className="me-2">แสดง:</span>
                    <select
                      className="form-select me-2"
                      style={{ width: 'auto', minWidth: '70px' }}
                      value={itemsPerPage}
                      onChange={(e) => {
                        const newItemsPerPage = parseInt(e.target.value);
                        setItemsPerPage(newItemsPerPage);
                        localStorage.setItem('itemsPerPageDropdown', newItemsPerPage);
                        setCurrentPage(1);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span>รายการต่อหน้า | แสดง {indexOfFirstItem + 1} ถึง {Math.min(indexOfLastItem, filteredItems.length)} จาก {filteredItems.length} รายการ</span>
                  </div>
                  <div className="d-flex align-items-center flex-wrap">
                    <Pagination
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      dataMasterList={filteredItems}
                      handlePageChange={handlePageChange}
                    />
                  </div>
                </div>
                <div className="text-muted" style={{ fontSize: "12px" }}>
                  {getDataTableKeyboardInstructions(false, true)}
                </div>
              </div>
            </div>
          </div>
          <div className="col-8 mb-3">
            <div className="justify-content-between align-items-center mb-3">
              <div className="row">
                <div className="col-6">
                  <p className="card-title mb-0">
                    ชื่อพนักงาน: {wtEmpName}
                  </p>
                </div>
                {(!isCheckboxEnabled && filteredDetailList.length > 0) &&
                  <div className="col-6 d-flex justify-content-end">
                    <button
                      className="btn d-flex align-items-center text-white me-2"
                      style={{
                        backgroundColor: 'rgb(239, 108, 0)',
                        fontSize: '16px',
                      }}
                      onClick={() => setIsCheckboxEnabled(!isCheckboxEnabled)}
                    >
                      <i className="fa fa-edit me-2" aria-hidden="true"></i>
                      เลือกคืนสินค้า
                    </button>
                  </div>
                }
              </div>
            </div>
            <div
              className="table-responsive"
              style={{
                maxHeight: '600px',
                overflowY: 'auto',
              }}
            >
              <table
                id="basic-datatables"
                className="table table-striped table-hover"
                style={{ minWidth: '100%' }}
              >
                <thead className="thead-dark">
                  <tr>
                    <th className="text-center" style={{ width: '2%' }}>ลำดับ</th>
                    <th className="text-center" style={{ width: '2%' }}>
                      <input
                        type="checkbox"
                        disabled={!isCheckboxEnabled}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const validItems = filteredDetailList.filter(item => item.WTDocStatus_Name !== 'ยกเลิก');
                          setSelectedItems(checked ? validItems : []);
                        }}
                        checked={
                          filteredDetailList.length > 0 &&
                          selectedItems.length === filteredDetailList.filter(item => item.WTDocStatus_Name !== 'ยกเลิก').length
                        }
                      />
                    </th>
                    <th className="text-center" style={{ width: '10%' }}>วันที่เอกสาร</th>
                    <th className="text-center" style={{ width: '10%' }}>เลขเอกสาร</th>
                    <th className="text-center" style={{ width: '10%' }}>สถานะ</th>
                    <th className="text-center" style={{ width: '15%' }}>รหัสสินค้า</th>
                    <th className="text-center" style={{ width: '54%' }}>ชื่อสินค้า</th>
                    <th className="text-center" style={{ width: '10%' }}>จำนวน</th>
                    <th className="text-center" style={{ width: '2%' }}>
                      <i className="fa fa-edit" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDetailList.length > 0 ? (
                    filteredDetailList
                      .filter((item) => item.Item_Is_Return === 0)
                      .map((item, index) => (
                        <tr
                          key={item.DT_Id || index + 1}
                          style={{
                            cursor: 'pointer',
                            backgroundColor: selectedItems.some(select => select.DT_Id === item.DT_Id) ? 'rgb(105, 188, 216)' : 'transparent',
                            transition: 'background-color 0.3s ease',
                          }}>
                          <td className="text-center">{index + 1}</td>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              disabled={!isCheckboxEnabled || item.WTDocStatus_Name === 'ยกเลิก'}
                              checked={selectedItems.some(select => select.DT_Id === item.DT_Id)}
                              onChange={() => handleCheckboxChange(item)}
                              style={{ cursor: 'pointer' }}
                            />
                          </td>
                          <td className="text-center">{formatDate(item.WTDoc_Date)}</td>
                          <td className="text-center">{item.WTDoc_No}</td>
                          <td className="text-center">
                            <button
                              className="btn"
                              style={{
                                backgroundColor: item.WTDocStatus_Colour,
                                color: 'white',
                                width: '100%'
                              }}
                            >
                              {item.WTDocStatus_Name}
                            </button>
                          </td>
                          <td className="text-center">{item.Item_Code}</td>
                          <td className="text-left">{item.Item_Name}</td>
                          <td className="text-center">{item.Item_Qty}</td>
                          <td className="text-center">
                            <button
                              hidden={item.WTDocStatus_Name === 'ยกเลิก'}
                              className="btn btn-sm"
                              onClick={() => onPageUpdate(item.WTDoc_No)}
                            >
                              <i className="fa fa-edit" />
                            </button>
                          </td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan="14">
                        <center>
                          <h5>ไม่พบข้อมูล</h5>
                        </center>
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  {isCheckboxEnabled && (
                    <tr>
                      <td colSpan="9">
                        <div className="d-flex justify-content-left mt-3">
                          <button
                            className="btn btn-lg btn-success me-3"
                            onClick={() => setShowModal(true)}
                            disabled={selectedItems.length === 0}
                          >
                            ยืนยันการคืนสินค้า
                          </button>
                          <button
                            className="btn btn-lg btn-danger"
                            onClick={() => {
                              setIsCheckboxEnabled(false);
                              setSelectedItems([]);
                            }}
                          >
                            ยกเลิกการคืนสินค้า
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Modal */}
                  <div className={`modal fade ${showModal ? 'show' : ''}`} tabIndex="-1" style={{ display: showModal ? 'block' : 'none' }}>
                    <div className="modal-dialog modal-dialog-centered" role="document">
                      <div className="modal-content">
                        <div className="modal-header">
                          <h5 className="modal-title fw-bold">ยืนยันการคืนสินค้า</h5>
                          <button type="button" className="btn-close" onClick={() => setShowModal(false)} aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                          <p>คุณแน่ใจว่าต้องการดำเนินการยืนยันการคืนสินค้าหรือไม่?</p>
                        </div>
                        <div className="modal-footer">
                          <button
                            type="button"
                            className="btn btn-lg w-25 shadow text-white"
                            style={{ backgroundColor: '#EF6C00', fontSize: '16px' }}
                            onClick={() => handleConfirmReturn()}
                          >
                            ยืนยัน
                          </button>
                          <button
                            type="button"
                            className="btn btn-lg w-25 shadow text-white"
                            style={{ backgroundColor: 'red', fontSize: '16px' }}
                            onClick={() => setShowModal(false)}
                          >
                            ยกเลิก
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </>;
};

export default Main;